import {
  Tr,
  Badge,
  Flex,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  Checkbox,
  Select,
  useToast,
  Td,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertsContext } from "providers/alerts";
import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiRequest } from "utils/api";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getErrorMessage } from "utils/errorHandler";
import colors from "utils/colors";
import { useCustomFormats } from "hooks/useCustomFormat";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { UserBox } from "modules/user/components/user-box";
import { useCategories } from "hooks/useCategories";

const FILE_TYPE_LABELS: { [key: string]: string } = {
  "application/pdf": "PDF",
  "text/csv": "CSV",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.ms-excel": "XLS",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/vnd.ms-powerpoint": "PPT",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
  "text/plain": "TXT",
  "application/zip": "ZIP",
  "application/x-rar-compressed": "RAR",
  "image/jpeg": "JPEG",
  "image/jpg": "JPG",
  "image/png": "PNG",
  "image/gif": "GIF",
  "image/webp": "WEBP",
  "image/svg+xml": "SVG",
  "audio/mpeg": "MP3",
  "audio/wav": "WAV",
  "audio/ogg": "OGG",
  "video/mp4": "MP4",
  "video/x-msvideo": "AVI",
  "video/quicktime": "MOV",
  "video/x-matroska": "MKV",
  "application/json": "JSON",
  "application/xml": "XML",
  "application/x-yaml": "YAML",
  "application/octet-stream": "BIN",
};

const FileTypeBadge = ({ file_type }: { file_type: string }) => {
  const fileColors: { [key: string]: { bg: string; text: string } } = {
    "application/pdf": { bg: "#EFE6FF", text: "#6200FF" }, //for PDF
    "text/csv": { bg: "#E5E9FF", text: "#2746E5" }, // for CSV
    "image/jpeg": { bg: "orange.500", text: "black" }, // for JPEG
    "image/png": { bg: "#E5F6FF", text: "#168FD2" }, // Example colors for PNG
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
      bg: "black",
      text: "white",
    }, // Example colors for XLSX
  };

  // Get background and font colors based on file type
  const { bg, text } = fileColors[file_type] || {
    bg: "primary",
    text: "white",
  };

  return (
    <Badge color={text} bg={bg}>
      {FILE_TYPE_LABELS[file_type] || file_type}
    </Badge>
  );
};

const DocumentStatusSelector = ({ status, documentId }: any) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useTenant();
  const canEdit = hasPermission(user, permissionConstants.uploadDoc);

  const { mutate: changeDocumentStatus, isPending } = useMutation({
    mutationFn: async (newStatus: string) => {
      return apiRequest<{ document: { id: string; status: string } }>(
        `/supabase/documents/${documentId}`,
        {
          status: newStatus,
        },
        "patch"
      );
    },

    onSuccess: data => {
      toast({
        title: "Document Status Changed",
        description: "The document status was successfully changed.",
        status: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["documents-for-loan-list"],
      });
      queryClient.invalidateQueries({ queryKey: ["documents-list"] });
    },
  });

  return (
    <Select
      size="sm"
      disabled={isPending || !canEdit}
      value={status ?? undefined}
      onChange={e => {
        changeDocumentStatus(e.target.value);
      }}
      width="max-content"
      placeholder="Select Status"
    >
      <option value="submitted">Submitted</option>
      <option value="under_review">Under Review</option>
      <option value="rejected">Rejected</option>
      <option value="accepted">Accepted</option>
    </Select>
  );
};

const DocumentCategorySelector = ({ category, documentId, status }: any) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useTenant();
  const { categories, isLoading } = useCategories();
  const canEdit = hasPermission(user, permissionConstants.editAndUpload);

  const { mutate: changeDocumentCategory, isPending } = useMutation({
    mutationFn: async (newCategory: string) => {
      return apiRequest<{ document: { id: string; category: string } }>(
        `/supabase/documents/${documentId}`,
        { category: newCategory, status },
        "patch"
      );
    },
    onSuccess: () => {
      toast({
        title: "Document Category Changed",
        description: "The document category was successfully changed.",
        status: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["documents-list"] });
      queryClient.invalidateQueries({ queryKey: ["documents-for-loan-list"] });
    },
  });

  return (
    <Select
      size="sm"
      disabled={isPending || !canEdit || isLoading}
      value={category ?? ""}
      onChange={e => changeDocumentCategory(e.target.value)}
      width="max-content"
      placeholder="Select Category"
    >
      {categories.map((cat: string) => (
        <option key={cat} value={cat}>
          {cat}
        </option>
      ))}
    </Select>
  );
};

type Document = {
  id: number;
  created_at: string;
  name: string;
  user: {
    id: string;
    type: string;
    avatar: string | null;
    is_active: boolean;
    job_title: string;
    last_name: string;
    first_name: string;
    company: {
      name: string;
    };
  };
  hide_from_borrower?: boolean;
  path: string;
  company: {
    name: string;
  };
  loan: {
    id: string;
    name: string;
  } | null;
  file_type: string;
  file_size: number;
  platform_id: string | null;
  platform: string | null;
  category: string | null;
  status: string | null;
  loan_item: string | null;
  draw_request: string | null;
  lender_company: string;
  borrower_company: string | null;
  folder_id: string | null;
  signatures: [];
};

const DocumentRow = ({
  document,
  columns,
  item,
  onDelete,
  onDownload,
  onRowSelect,
  selectedRows,
}: {
  document: Document;
  columns: string[];
  item?: any;
  onDelete: (id: number) => void;
  onDownload: (url: string) => void;
  onRowSelect?: (id: number) => void;
  selectedRows?: any;
}) => {
  const navigate = useNavigate();
  const { formatDate } = useCustomFormats();
  const { showError, showSuccess } = useContext(AlertsContext);
  const { user } = useTenant();
  const queryClient = useQueryClient();

  const downloadDocument = async (id: number) => {
    try {
      const response = await apiRequest("/supabase/documents/download", {
        id,
      });

      onDownload(response.signedUrl);
    } catch (err) {
      showError({
        title: "Download Failed",
        description: getErrorMessage(err),
      });
    }
  };

  const deleteDocument = async (id: number) => {
    try {
      await apiRequest("/supabase/documents/delete", {
        ids: [id],
      });

      onDelete(id);
      showSuccess({
        title: "Document Deleted",
        description: "The document was successfully deleted.",
      });
      await queryClient.invalidateQueries({
        queryKey: ["documents-list"],
      });
    } catch (err) {
      showError({
        title: "Delete Failed",
        description: getErrorMessage(err),
      });
    }
  };

  const handleRowSelect = (id: number) => {
    onRowSelect?.(id);
  };
  let foundRow = selectedRows?.find((row: any) => row === document?.id);
  const handleCheckboxChange = () => handleRowSelect(document.id);

  const getRowBackground = (id: any) => {
    if (selectedRows && selectedRows.length > 0) {
      const isSelected = selectedRows.includes(id);
      if (isSelected) return colors.GRAY_50;
      else return "white";
    } else {
      return "white";
    }
  };

  const checkDownloadPermission = () => {
    if (hasPermission(user, permissionConstants.downloadDocuments)) {
      return true;
    }
    if (!isPermissionEnabled(user)) {
      // no permissions
      return true;
    }
    return false;
  };

  const checkEditUploadPermission = () => {
    if (hasPermission(user, permissionConstants.editAndUpload)) {
      return true;
    }
    if (!isPermissionEnabled(user)) {
      // no permissions
      return true;
    }
    return false;
  };

  const handleDownload = (id: number) => {
    if (checkDownloadPermission()) {
      downloadDocument(id);
    } else
      showError({
        title: "Download Failed",
        description: "You do not have permission to download this document.",
      });
  };

  const handleView = (id: number) => {
    if (checkEditUploadPermission()) {
      navigate(`/documents/${id}`);
    } else
      showError({
        title: "View Failed",
        description: "You do not have permission to view this document.",
      });
  };

  const toggleHideFromBorrower = async (id: number) => {
    try {
      await apiRequest("/supabase/documents/update", {
        id,
        hide_from_borrower: !document.hide_from_borrower,
      });

      showSuccess({
        title: "Document Visibility Updated",
        description: document.hide_from_borrower
          ? "Document is now visible to borrowers"
          : "Document is now hidden from borrowers",
      });

      await queryClient.invalidateQueries({
        queryKey: ["documents-list"],
      });
    } catch (err) {
      showError({
        title: "Update Failed",
        description: getErrorMessage(err),
      });
    }
  };

  return (
    <Tr key={document.id} backgroundColor={getRowBackground(document.id)}>
      {columns.includes("select") && (
        <Td>
          <Flex justifyContent="center">
            <Checkbox colorScheme="teal" isChecked={!!foundRow} onChange={handleCheckboxChange} />
          </Flex>
        </Td>
      )}
      {columns.includes("Name") && (
        <Td>
          <Link to={`/documents/${document.id}`}>{document.name}</Link>
        </Td>
      )}
      {columns.includes("Type") && (
        <Td>
          <FileTypeBadge file_type={document.file_type} />
        </Td>
      )}
      {columns.includes("Uploader") && (
        <Td>
          <UserBox user={document.user} />
        </Td>
      )}
      {columns.includes("Uploaded") && <Td>{formatDate(document.created_at)}</Td>}

      {columns.includes("Checklist Item") && (
        <Td>
          {item ? (
            <Flex flexDir="column">
              <Text color="black" fontSize="14px" fontWeight="500" mb="6px">
                {item.title}
              </Text>
              <Text color="gray.200" fontSize="12px" lineHeight="12px" mt="0px">
                {item.category}
              </Text>
            </Flex>
          ) : (
            <Box></Box>
          )}
        </Td>
      )}
      {columns.includes("Loan") && (
        <Td>
          <Link to={`/loans/${document.loan?.id}`}>{document.loan?.name}</Link>
        </Td>
      )}
      {columns.includes("Category") && (
        <Td>
          {user?.type === "lender" ? (
            <DocumentCategorySelector
              category={document.category}
              documentId={document.id}
              status={document.status}
            />
          ) : (
            <Text textTransform={"capitalize"}>{document.category ?? "Not provided"}</Text>
          )}
        </Td>
      )}

      {columns.includes("Status") && (
        <Td>
          {user?.type === "lender" ? (
            <DocumentStatusSelector status={document.status} documentId={document.id} />
          ) : (
            <Text textTransform={"capitalize"}>{document?.status ?? "submitted"}</Text>
          )}
        </Td>
      )}

      <Td textAlign="center">
        <Menu>
          <MenuButton
            as={IconButton}
            background="transparent"
            color="gray.200"
            borderWidth="0px"
            _hover={{
              background: "transparent",
              color: "primary",
            }}
            aria-label="document-actions"
            icon={<FontAwesomeIcon icon={["far", "ellipsis-vertical"]} />}
          />
          <MenuList>
            <MenuItem onClick={() => handleView(document.id)}>View</MenuItem>
            <MenuItem onClick={() => handleDownload(document.id)}>Download</MenuItem>
            {user.type !== "borrower" && (
              <MenuItem onClick={() => toggleHideFromBorrower(document.id)}>
                {document.hide_from_borrower ? "Show to Borrower" : "Hide from Borrower"}
              </MenuItem>
            )}
            <MenuItem onClick={() => deleteDocument(document.id)}>Delete</MenuItem>
          </MenuList>
        </Menu>
      </Td>
    </Tr>
  );
};

export default DocumentRow;
